import * as React from 'react';
import InputAdornment from '@material-ui/core/InputAdornment';
import Schedule from '@material-ui/icons/Schedule';
import CalendarToday from '@material-ui/icons/CalendarToday';
import Paper from '@material-ui/core/Paper';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import OutlinedInput from '@material-ui/core/OutlinedInput';
import { makeStyles } from '@material-ui/core/styles';
import SingleSelect from './SingleSelect';
import MultipleSelect from './MultipleSelect';
import Toggle from './Toggle';
import Signature from './Signature';
import Photo from './Photo';
import CheckBox from './Checkbox';
import Label from './Label';
import { SectionField, SectionFieldContainerProps } from '../typings';
import { getTemplateValues, setTemplateValues, FieldInputType } from '../utils/templateValues';

const useStyles = makeStyles({
  root: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '80%',
    height: '100%',
    zIndex: 0
  },
  shrink: {
    right: 'unset'
  }
});

function renderFieldComponent(isMobile: boolean, sectionFieldDetails: SectionField, sectionRank: number) {
  const [value, setValue] = React.useState('');
  const [secondValue, setSecondValue] = React.useState('');
  const classes = useStyles();

  const handleValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!isMobile) {
      const templateValues = getTemplateValues();
      const targetSection = templateValues.templateSections.find((x) => x.rank === sectionRank);
      const targetField = targetSection.sectionFields.find((y: any) => y.rank === sectionFieldDetails.rank);
      targetField.value = event.target.value;
      // works because of shallow referencing
      setTemplateValues(templateValues);
    }
    setValue(event.target.value);
  };
  const handleSecondValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!isMobile) {
      const templateValues = getTemplateValues();
      const targetSection = templateValues.templateSections.find((x) => x.rank === sectionRank);
      const targetField = targetSection.sectionFields.find((y: any) => y.rank === sectionFieldDetails.rank);
      targetField.value = event.target.value;
      // works because of shallow referencing
      setTemplateValues(templateValues);
    }
    setSecondValue(event.target.value);
  };
  const labelFor = `${sectionFieldDetails.name}-${sectionFieldDetails.rank}-${isMobile}`;

  switch (sectionFieldDetails.fieldInputType) {
    case FieldInputType.ShortText:
      return (
        <FormControl fullWidth variant='outlined' size={isMobile ? 'small' : 'medium'} required={sectionFieldDetails.isMandatory}>
          <InputLabel classes={{ root: classes.root }} htmlFor={labelFor}>
            {sectionFieldDetails.name}
          </InputLabel>
          <OutlinedInput id={labelFor} value={value} onChange={handleValueChange} label={sectionFieldDetails.name} />
        </FormControl>
      );
    case FieldInputType.LongText:
      return (
        <FormControl fullWidth variant='outlined' size={isMobile ? 'small' : 'medium'} required={sectionFieldDetails.isMandatory}>
          <InputLabel classes={{ root: classes.root }} htmlFor={labelFor}>
            {sectionFieldDetails.name}
          </InputLabel>
          <OutlinedInput id={labelFor} value={value} onChange={handleValueChange} label={sectionFieldDetails.name} multiline rows={4} />
        </FormControl>
      );
    case FieldInputType.NumberInteger:
      return (
        <FormControl fullWidth variant='outlined' size={isMobile ? 'small' : 'medium'} required={sectionFieldDetails.isMandatory}>
          <InputLabel classes={{ root: classes.root }} htmlFor={labelFor}>
            {sectionFieldDetails.name}
          </InputLabel>
          <OutlinedInput id={labelFor} value={value} onChange={handleValueChange} label={sectionFieldDetails.name} />
        </FormControl>
      );
    case FieldInputType.NumberDecimal:
      return (
        <FormControl fullWidth variant='outlined' size={isMobile ? 'small' : 'medium'} required={sectionFieldDetails.isMandatory}>
          <InputLabel classes={{ root: classes.root }} htmlFor={labelFor}>
            {sectionFieldDetails.name}
          </InputLabel>
          <OutlinedInput id={labelFor} value={value} onChange={handleValueChange} label={sectionFieldDetails.name} />
        </FormControl>
      );
    case FieldInputType.DateAndTime:
      return (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
          <FormControl fullWidth variant='outlined' size={isMobile ? 'small' : 'medium'} required={sectionFieldDetails.isMandatory}>
            <InputLabel classes={{ root: classes.root, shrink: classes.shrink }} htmlFor={`${labelFor}date`}>
              {sectionFieldDetails.name}
            </InputLabel>
            <OutlinedInput
              id={`${labelFor}date`}
              value={value}
              onChange={handleValueChange}
              label={sectionFieldDetails.name}
              endAdornment={<InputAdornment position='end'>{!isMobile && <CalendarToday />}</InputAdornment>}
            />
          </FormControl>
          <FormControl fullWidth variant='outlined' size={isMobile ? 'small' : 'medium'} required={sectionFieldDetails.isMandatory}>
            <InputLabel classes={{ root: classes.root, shrink: classes.shrink }} htmlFor={`${labelFor}time`}>
              {sectionFieldDetails.name}
            </InputLabel>
            <OutlinedInput
              id={`${labelFor}time`}
              value={secondValue}
              onChange={handleSecondValueChange}
              label={sectionFieldDetails.name}
              endAdornment={<InputAdornment position='end'>{!isMobile && <Schedule />}</InputAdornment>}
            />
          </FormControl>
        </div>
      );
    case FieldInputType.Date:
      return (
        <FormControl fullWidth variant='outlined' size={isMobile ? 'small' : 'medium'} required={sectionFieldDetails.isMandatory}>
          <InputLabel classes={{ root: classes.root }} htmlFor={labelFor}>
            {sectionFieldDetails.name}
          </InputLabel>
          <OutlinedInput
            id={labelFor}
            value={value}
            onChange={handleValueChange}
            label={sectionFieldDetails.name}
            endAdornment={<InputAdornment position='end'>{!isMobile && <CalendarToday />}</InputAdornment>}
          />
        </FormControl>
      );
    case FieldInputType.Time:
      return (
        <FormControl fullWidth variant='outlined' size={isMobile ? 'small' : 'medium'} required={sectionFieldDetails.isMandatory}>
          <InputLabel classes={{ root: classes.root }} htmlFor={labelFor}>
            {sectionFieldDetails.name}
          </InputLabel>
          <OutlinedInput
            id={labelFor}
            value={value}
            onChange={handleValueChange}
            label={sectionFieldDetails.name}
            endAdornment={<InputAdornment position='end'>{!isMobile && <Schedule />}</InputAdornment>}
          />
        </FormControl>
      );
    case FieldInputType.MultiSelect:
      return <MultipleSelect isMobile={isMobile} sectionFieldDetails={sectionFieldDetails} sectionRank={sectionRank} />;
    case FieldInputType.SingleSelect:
      return <SingleSelect isMobile={isMobile} sectionFieldDetails={sectionFieldDetails} sectionRank={sectionRank} />;
    case FieldInputType.Toggle:
      return <Toggle isMobile={isMobile} sectionFieldDetails={sectionFieldDetails} sectionRank={sectionRank} />;
    case FieldInputType.Signature:
      return <Signature isMobile={isMobile} sectionFieldDetails={sectionFieldDetails} sectionRank={sectionRank} />;
    case FieldInputType.Photo:
      return <Photo isMobile={isMobile} sectionFieldDetails={sectionFieldDetails} sectionRank={sectionRank} />;
    case FieldInputType.Checkbox:
      return <CheckBox isMobile={isMobile} sectionFieldDetails={sectionFieldDetails} sectionRank={sectionRank} />;
    case FieldInputType.Label:
      return <Label isMobile={isMobile} sectionFieldDetails={sectionFieldDetails} sectionRank={sectionRank} />;
    default:
      return (
        <div>
          {sectionFieldDetails.name} - {sectionFieldDetails.fieldInputType}
        </div>
      );
  }
}

const SectionFieldContainer: React.FC<SectionFieldContainerProps> = (sectionFieldProps) => {
  const { isMobile, sectionFieldDetails, sectionRank } = sectionFieldProps;
  const isLabel = sectionFieldDetails.fieldInputType === FieldInputType.Label;

  return (
    <div style={{ paddingTop: isMobile ? '0' : '1rem', paddingBottom: isMobile ? '0' : '1rem' }}>
      {isMobile && !isLabel && (
        <Paper style={{ padding: '1rem', margin: '6px' }}>{renderFieldComponent(isMobile, sectionFieldDetails, sectionRank)}</Paper>
      )}
      {isMobile && isLabel && (
        <div style={{ padding: '8px 16px', margin: '4px 6px 0' }}>{renderFieldComponent(isMobile, sectionFieldDetails, sectionRank)}</div>
      )}
      {!isMobile && <div>{renderFieldComponent(isMobile, sectionFieldDetails, sectionRank)}</div>}
    </div>
  );
};

export default SectionFieldContainer;
